<template>
    <div>
        <div class="w-full flex flex-column align-items-center mt-5" style="color: var(--theme-font-color);">
            
            <div v-if="!authorizationCallback" class="w-full flex flex-column align-items-center justify-content-center" style="max-width: 50rem">
                <div style="height: 2.9rem;"></div>
                <span style="color: var(--theme-font-color); font-size: var(--register-maintitle-font-size); font-weight: var(--register-maintitle-font-weight);">Email Confirmation</span>

                <div v-if="!isConfirmed">
                    <div v-if="!errorConfirmation" style="height: 65vh;" class="flex align-items-center justify-content-center">
                        <LoadingSpinner/>
                    </div>
                    <div v-else class="w-full flex flex-column align-items-center justify-content-center mt-5" style="font-size: var(--text-font-size); font-weight: var(--text-font-weight);">
                        <SvgError class="mb-4" className="registerconfirm-svg" color="var(--mydanger-color)"/>
                        <span v-if="confirmationErrorMessage" class="text-center" style="font-size: var(--title-font-size); font-weight: var(--text-font-weight);">{{ confirmationErrorMessage }}</span>

                        <span style="font-size: var(--title-font-size); font-weight: var(--text-font-weight); gap: 0.4rem;" class="text-center mt-2 align-items-center">
                            Please try to
                            <router-link unstyled :to="{ path: '/register'}" style="text-decoration: none;">
                                <span id="clickable-text" class="cursor-pointer">register</span>
                            </router-link>
                            again.
                        </span>

                    </div>
                </div>
                <div v-if="isConfirmed" class="flex flex-column align-items-center mt-5">
                    <SvgSuccess class="mb-4" className="registerconfirm-svg" color="var(--mysuccess-color)"/>
                    <span class="text-center" style="font-size: var(--title-font-size); font-weight: var(--text-font-weight);">{{ successMessage }}</span>

                    <span style="font-size: var(--title-font-size); font-weight: var(--text-font-weight); gap: 0.4rem;" class="text-center mt-2 align-items-center">
                        You can now
                        <router-link unstyled :to="{ path: '/login'}" style="text-decoration: none;">
                            <span id="clickable-text" class="cursor-pointer">sign in</span>
                        </router-link>
                        !
                    </span>
                </div>
            </div>

            <div v-else class="w-full flex flex-column align-items-center p-6 gap-4" style="color: var(--theme-font-color); min-width: 15rem; max-width: 35rem; width: 88vw; border: 1px solid var(--theme-color5); border-radius: 18px;">
                <span style="color: var(--theme-font-color); font-size: var(--login-maintitle-font-size); font-weight: var(--login-maintitle-font-weight);" class="mx-5 px-8 text-center">
                    <span>Sign in as </span>
                    <span>{{ loggedInEmail }}</span>
                </span>

                <div class="flex flex-column w-full gap-2 align-items-center">
                    <ButtonModule type="primary" severity="normal" :label="(applicationName && applicationName != '') ? `Open ${applicationName}` : 'Open the Softron Application'" @click="clickedOpenSoftronApplication"/>
                    <span style="font-size: var(--login-subtext-font-size); font-weight: var(--login-subtext-font-weight);">
                        Stay in browser?
                        <span id="clickable-text" class="cursor-pointer" style="text-decoration: none; font-size: var(--login-subtext-font-size); font-weight: var(--login-subtext-font-weight);" @click="router.push('/licenses');">Go to 'Softron License Manager'.</span>
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
// --- IMPORTS ---
import { ref, toRefs, watchEffect, onMounted } from 'vue';
// PrimeVue
import SvgSuccess from '@/components/SVGs/SvgSuccess.vue'
import SvgError from '@/components/SVGs/SvgError.vue'
// vue
import LoadingSpinner from '@/components/Modules/LoadingSpinner.vue';
import ButtonModule from '@/components/Modules/ButtonModule.vue'
// Others
import { useRouter } from 'vue-router'
import { useStore } from 'vuex';
import toastService from '@/composables/toastService';
import UserUtils from '@/composables/UserUtils'

// API
import useApiRequest from '@/composables/HTTPS/useApiRequest';
const { fetchRegisterConfirm } = useApiRequest()
const httpRegisterConfirmLoading = ref(false)
const httpRegisterConfirmData = ref(null)

const router = useRouter()
const store = useStore()
const { loginUser } = UserUtils(store, router)

// --- PROPS ---
const props = defineProps({
    token: String,
    invitation: {
        type: String,
        default: null
    }
})
const { token, invitation } = toRefs(props)

// --- REDIRECT IF NO ATTACHED TOKEN ---
watchEffect(() => {
    if (!token.value) { router.push({ path: '/login' }) }
})

// --- VARIABLES ---
const isConfirmed = ref(false)
const errorConfirmation = ref(false)

// --- CONFIRMATION ---
const authorizationCallback = ref(null)
const loggedInEmail = ref(null)
const applicationName = ref(null)
const successMessage = ref("Your account registration has been successfully confirmed.")
const confirmationErrorMessage = ref(null)
const confirm = async () => {
    confirmationErrorMessage.value = null
    isConfirmed.value = false
    errorConfirmation.value = false
    // Send HTTP request
    httpRegisterConfirmLoading.value = true
    try {
        if (invitation.value) {
            httpRegisterConfirmData.value = await fetchRegisterConfirm({ token: token.value, invitation: invitation.value })
        } else {
            httpRegisterConfirmData.value = await fetchRegisterConfirm({ token: token.value })
        }
        isConfirmed.value = true
        errorConfirmation.value = false

        // Classic registration confirmation (from a registration from the platform)
        if (httpRegisterConfirmData.value.account_info) {
            // Login the user
            loginUser(httpRegisterConfirmData.value)
            // Redirect to /licenses
            router.push({ path: '/licenses' })
        }
        // Application registration confirmation (from a registration from the application) => should propose to redirect to the application
        if (httpRegisterConfirmData.value.authorization_callback) {
            // Update successful login info
            loggedInEmail.value = httpRegisterConfirmData.value.email
            applicationName.value = httpRegisterConfirmData.value.application_name
            authorizationCallback.value = httpRegisterConfirmData.value.authorization_callback
            // Callback to the app
            window.location.href = authorizationCallback.value
        }
    } catch (err) {
        handleRegisterConfirmError(err)
    } finally {
        httpRegisterConfirmLoading.value = false
    }
}
// Handle an error from the server
const handleRegisterConfirmError = (error) => {
    try {
        if (error.response == null) { // If there is no .response key, then it means it is not an error from the server, it did not reach it
            toastService.add({ severity: 'error', summary: `Registration Confirmation: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
            return;
        }
        errorConfirmation.value = true
        const errorStatus = error.response.status
        const errorCode = error.response.data.error_code
        const errorMessage = error.response.data.error_message
        switch (errorStatus) {
            case 400: // Missing info = MISSING_OBJ / MISSING_OBJ_ELEM (400) => toast
                if (errorCode == 'MISSING_OBJ' || errorCode == 'MISSING_OBJ_ELEM') {
                    confirmationErrorMessage.value = "It seems that the request body does not contain the necessary information."
                    //toastService.add({ severity: 'warn', summary: "Registration Confirmation: Missing Information.", detail: 'It seems that the request body does not contain the necessary information.', life: 5000 });
                }
                else {  toastService.add({ severity: 'error', summary: `Registration Confirmation: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            case 401: // The token attached to the request is not valid : expired or a more recent confirmation link has been sent = CONFIRMATION_EMAIL_TOKEN_INVALID (401) => toast
                if (errorCode == 'CONFIRMATION_EMAIL_TOKEN_INVALID') {
                    confirmationErrorMessage.value = errorMessage
                    //toastService.add({ severity: 'warn', summary: "Registration Confirmation: Invalid Confirmation.", detail: 'It seems that the confirmation link is not valid anymore. Try to register again.', life: 5000 });
                }
                else if (errorCode == 'INVALID_AUTHORIZATION_SESSION') { // This case occurs if the confirmation token is valid (account has been validated), but the session for redirection to Softron Application is invalid (i.e. expired)
                    isConfirmed.value = true
                    errorConfirmation.value = false
                    //toastService.add({ severity: 'warn', summary: "Registration Confirmation: Redirection Session Expired.", detail: 'Your account has been successfully confirmed but the session to redirect to your application is expired. Try to login again in your application with your account.', life: 8000 });
                    router.push({ path: '/login' })
                }
                else {  toastService.add({ severity: 'error', summary: `Registration Confirmation: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            case 409: // Account already registered and confirmed = CONFLICT (410) => toast
                if (errorCode == 'ACCOUNT_ALREADY_CONFIRMED') {
                    errorConfirmation.value = false
                    isConfirmed.value = true
                    successMessage.value = "It seems that the account was already confirmed."
                    //toastService.add({ severity: 'warn', summary: "Registration Confirmation: Account Already Confirmed.", detail: 'It seems that the account is already confirmed.', life: 5000 });
                }
                else {  toastService.add({ severity: 'error', summary: `Registration Confirmation: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            case 410: // Confirmation exists but is expired = CONFIRMATION_EMAIL_TOKEN_INVALID (410) => toast
                if (errorCode == 'CONFIRMATION_EMAIL_TOKEN_INVALID') {
                    confirmationErrorMessage.value = errorMessage
                    //toastService.add({ severity: 'warn', summary: "Registration Confirmation: Invalid Confirmation.", detail: 'It seems that the confirmation link is not valid anymore. Try to register again.', life: 5000 });
                }
                else {  toastService.add({ severity: 'error', summary: `Registration Confirmation: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            case 500: // DATABASE_ERROR (500) => toast
                if (errorCode == 'DATABASE_ERROR') {
                    confirmationErrorMessage.value = "Sorry, it seems that there is an internal error. Try later.'"
                    //toastService.add({ severity: 'error', summary: "Registration Confirmation: Internal Error.", detail: 'Sorry, it seems that there is an internal error. Try later.', life: 5000 });
                }
                else {  toastService.add({ severity: 'error', summary: `Registration Confirmation: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            default:
                toastService.add({ severity: 'error', summary: `Registration Confirmation: Error ${errorStatus}.`, detail: errorMessage, life: 12000 });
                break;
        }
    } catch {
        toastService.add({ severity: 'error', summary: `Registration Confirmation: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
    }
}

// --- Open Softron Application ---
const clickedOpenSoftronApplication = () => {
    window.location.href = authorizationCallback.value
}

// --- Make the register_confirm request on mounted ! ---
onMounted(() => {
    confirm()
})
</script>

<style scoped>
:deep(.p-progress-spinner-circle) {
    stroke: var(--myprimary-color);
    animation: p-progress-spinner-dash 1.5s ease-in-out infinite !important;
}
/* Redirect */
#clickable-text {
    text-decoration: none;
    color: var(--button-normal-color);
}
#clickable-text:visited {
    color: var(--button-normal-color);
}
#clickable-text:hover {
    border-bottom: 1px solid var(--button-normal-color);
}
</style>